import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import Select from "antd/es/select";
import Switch from "antd/es/switch";
import { useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";

import ActivityTypeChip from "@app/component/activity/ActivityTypeChip";
import { getActivityTypes } from "@app/store/activity";
import { RootState } from "@app/store/rootReducer";
import activityTypeTagRender from "@app/util/ui/activityTypeTagRender";
import useActivityTypeOptions from "@app/util/ui/useActivityTypeOptions";

import styles from "./FrequencyPreview.module.scss";
import messages from "./messages";

interface Props {
  activityTypes: ActivityType[];
  allActivityTypes: boolean;
  onSetActivityTypes: (types: ActivityType["id"][]) => void;
  onSetAllActivityTypes: (allActivityTypes: boolean) => void;
  readonly?: boolean;
  selectedActivityTypeIds: ActivityType["id"][];
}

const ActivityTypes = ({
  activityTypes,
  allActivityTypes,
  onSetActivityTypes,
  onSetAllActivityTypes,
  readonly,
  selectedActivityTypeIds,
}: Props) => {
  const intl = useIntl();

  const activityTypeOptions = useActivityTypeOptions(
    activityTypes,
    selectedActivityTypeIds,
    useCallback((type: ActivityType) => !type.active, [])
  );

  const selectedActivityTypes = useMemo(
    () => activityTypes.filter(({ id }) => selectedActivityTypeIds.includes(id)),
    [activityTypes, selectedActivityTypeIds]
  );

  return (
    <>
      <Row align="middle" className={styles.line} justify="space-between">
        <Col>{intl.formatMessage(messages.acceptAllType)}</Col>
        <Col>
          {readonly ? (
            <span className={styles.staticValue}>
              {intl.formatMessage(messages.allActivityTypesStatic, { allActivityTypes })}
            </span>
          ) : (
            <Switch checked={allActivityTypes} onChange={onSetAllActivityTypes} />
          )}
        </Col>
      </Row>
      {!allActivityTypes &&
        (readonly ? (
          <div className={styles.readonlyActivityTypes}>
            {selectedActivityTypes.map((type) => (
              <ActivityTypeChip activityType={type} key={type.id} />
            ))}
          </div>
        ) : (
          <div className={styles.line}>
            <Select<ActivityType["id"][]>
              className={styles.activityTypes}
              dropdownMatchSelectWidth={false}
              filterOption={defaultFilterOption}
              mode="multiple"
              onChange={onSetActivityTypes}
              options={activityTypeOptions}
              placeholder={intl.formatMessage(messages.select)}
              tagRender={activityTypeTagRender}
              value={selectedActivityTypeIds}
            />
          </div>
        ))}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
});

export default connect(mapStateToProps)(ActivityTypes);
